import { Injectable } from '@angular/core';
import { environment } from '@apps/clos-sessions/environments';
import { AuthState } from '@apps/clos-sessions/state/auth';
import { retry, take, tap, timeout } from 'rxjs/operators';
import { Action, createSelector, State, StateContext, Store } from '@ngxs/store';
import { patch, updateItem, removeItem, append } from '@ngxs/store/operators';
import {
  CloseSession,
  CreateSession,
  DeclineInvitation,
  FetchInvitation,
  RefreshMediaToken,
  RefreshSession,
  SetActiveSession,
  JoinSession,
  EnsureSessionInviteLinks,
} from './remote-sessions.actions';
import { RemoteSessionsApi } from './remote-sessions.api';
import { ClosSession, ClosSessionInvitation } from './types';
import { SessionStatsResolver } from './session-stats';
import { SignOut } from '@apps/clos-sessions/state/auth';

/**
 * A session refresh attempt that answers later than this is treated as failed.
 * Worst case per dispatch: TIMEOUT * (RETRY_COUNT + 1) + RETRY_DELAY * RETRY_COUNT.
 */
export const REFRESH_SESSION_TIMEOUT_MS = 5_000;

/** A join that never answers (network drop, server hang) must not spin the reconnect UI forever */
export const JOIN_SESSION_TIMEOUT_MS = 10_000;

/** Same for a media-token refresh. It is never retried here: the reconnect loop owns the back-off. */
export const REFRESH_MEDIA_TOKEN_TIMEOUT_MS = 10_000;

/** Extra attempts after the first failed session refresh */
export const REFRESH_SESSION_RETRY_COUNT = 2;

/** Pause between session refresh attempts */
export const REFRESH_SESSION_RETRY_DELAY_MS = 1000;

export interface RemoteSessionsStateModel {
  sessions: ClosSession[];
  invitations: ClosSessionInvitation[];
}

const defaults: RemoteSessionsStateModel = {
  sessions: [],
  invitations: [],
};

@State<RemoteSessionsStateModel>({
  name: 'remoteSessions',
  defaults,
})
@Injectable()
export class RemoteSessionsState {
  static sessions = createSelector(
    [RemoteSessionsState],
    ({ sessions }: RemoteSessionsStateModel) => sessions,
  );

  static invitations = createSelector(
    [RemoteSessionsState],
    ({ invitations }: RemoteSessionsStateModel) => invitations,
  );

  static sessionById = (sessionId: string) => {
    return createSelector([RemoteSessionsState.sessions], (sessions: ClosSession[]) => {
      return sessions.find(({ id }) => sessionId === id) || null;
    });
  };

  static sessionByGuid = (guid: string) => {
    return createSelector([RemoteSessionsState.sessions], (sessions: ClosSession[]) => {
      return sessions.find((session) => guid === session.guid) || null;
    });
  };

  static hasSession = (sessionId: string) => {
    return createSelector(
      [RemoteSessionsState.sessionById(sessionId)],
      (session: ClosSession | null) => !!session,
    );
  };

  static sessionStats = (sessionId: string) => {
    return createSelector(
      [RemoteSessionsState.sessionById(sessionId)],
      (session: ClosSession | null) => {
        if (!session) {
          return null;
        }

        return new SessionStatsResolver(session).resolve();
      },
    );
  };

  static invitationBySessionId = (sessionId: string) => {
    return createSelector(
      [RemoteSessionsState.invitations],
      (invitations: ClosSessionInvitation[]) => {
        return invitations.find(({ session: { id } }) => sessionId === id) || null;
      },
    );
  };

  // region Deprecated selectors
  /* eslint-disable @typescript-eslint/member-ordering */
  /** @deprecated - use {@link RemoteSessionsState.hasSession} instead */
  static hasActiveSession = createSelector(
    [RemoteSessionsState],
    ({ sessions }: RemoteSessionsStateModel) => sessions.length > 0,
  );

  /** @deprecated - use {@link RemoteSessionsState.sessionById} instead */
  static activeSession = createSelector(
    [RemoteSessionsState],
    ({ sessions: [currentActiveSession] }: RemoteSessionsStateModel) => {
      return currentActiveSession || null;
    },
  );

  /** @deprecated - use {@link RemoteSessionsState.sessionStats} instead */
  static activeRoleTakenByUserIds = createSelector(
    [RemoteSessionsState.activeSession],
    (activeSession: ClosSession | null) => {
      if (!activeSession) {
        return [];
      }

      return SessionStatsResolver.activeRolesState(activeSession).users;
    },
  );

  /** @deprecated - use {@link RemoteSessionsState.sessionStats} instead */
  static photographerExist = createSelector(
    [RemoteSessionsState.activeSession],
    (activeSession: ClosSession | null) => !!activeSession?.photographerId,
  );

  /** @deprecated - use {@link RemoteSessionsState.sessionStats} instead */
  static modelExist = createSelector(
    [RemoteSessionsState.activeSession],
    (activeSession: ClosSession | null) => !!activeSession?.modelId,
  );

  /** @deprecated - use {@link RemoteSessionsState.sessionById} to extract the observers */
  static observers = createSelector(
    [RemoteSessionsState.activeSession],
    (activeSession: ClosSession | null) => activeSession?.observers || [],
  );

  /** @deprecated - use {@link RemoteSessionsState.sessionStats} instead */
  static observerExist = createSelector(
    [RemoteSessionsState.observers],
    (observers: string[]) => observers.length > 0,
  );

  /** @deprecated - use {@link RemoteSessionsState.sessionStats} instead */
  static countOfParticipants = createSelector(
    [RemoteSessionsState.activeSession],
    (activeSession: ClosSession | null) => {
      if (!activeSession) {
        return 0;
      }

      return SessionStatsResolver.participantsCount(activeSession);
    },
  );
  /* eslint-enable @typescript-eslint/member-ordering */
  // endregion

  constructor(
    protected api: RemoteSessionsApi,
    protected store: Store,
  ) {}

  /** Account-scoped data must not survive a logout (the next account can sign in without a reload). */
  @Action(SignOut)
  protected onSignOut({ setState }: StateContext<RemoteSessionsStateModel>): void {
    setState(defaults);
  }

  @Action(SetActiveSession)
  protected onSetActiveSession(
    { patchState }: StateContext<RemoteSessionsStateModel>,
    { payload }: SetActiveSession,
  ): void {
    patchState({ sessions: [payload] });
  }

  @Action(CreateSession)
  protected onCreateSession(
    { setState }: StateContext<RemoteSessionsStateModel>,
    { payload }: CreateSession,
  ) {
    return this.api.createSession(payload).pipe(
      tap((session) => {
        setState(
          patch({
            sessions: append([session]),
          }),
        );
      }),
    );
  }

  @Action(JoinSession)
  protected onJoinSession(
    { setState, getState }: StateContext<RemoteSessionsStateModel>,
    { payload }: JoinSession,
  ) {
    return this.api.joinSession(payload).pipe(
      // The request answers once; do not wait for a completion the token-refresh interceptor path never sends
      take(1),
      timeout(JOIN_SESSION_TIMEOUT_MS),
      tap((session) => {
        const exists = getState().sessions.some(({ id }) => id === session.id);
        setState(
          patch({
            sessions: exists
              ? updateItem<ClosSession>(
                  ({ id }) => id === session.id,
                  (current) => {
                    // A join answer without a token, or without a LiveKit descriptor,
                    // must not erase the working ones: only a fresh descriptor replaces them
                    const media = session.media ?? current.media;

                    return {
                      ...current,
                      ...session,
                      roomToken: session.roomToken || current.roomToken,
                      ...(media ? { media } : {}),
                    };
                  },
                )
              : append([session]),
            invitations: removeItem<ClosSessionInvitation>(
              (invitation) => invitation.session.id === session.id,
            ),
          }),
        );
      }),
    );
  }

  @Action(CloseSession)
  protected onCloseActiveSession(
    { setState }: StateContext<RemoteSessionsStateModel>,
    { payload }: CloseSession,
  ): void {
    setState(
      patch({
        sessions: removeItem<ClosSession>(({ id }) => id === payload.session.id),
      }),
    );
  }

  @Action(RefreshSession, {
    cancelUncompleted: true,
  })
  protected onRefreshSession(
    { setState }: StateContext<RemoteSessionsStateModel>,
    { payload }: RefreshSession,
  ) {
    return this.api.getSession({ sessionId: payload.session.id }).pipe(
      // The request answers once; do not wait for a completion the token-refresh interceptor path never sends
      take(1),
      timeout(REFRESH_SESSION_TIMEOUT_MS),
      retry({ count: REFRESH_SESSION_RETRY_COUNT, delay: REFRESH_SESSION_RETRY_DELAY_MS }),
      tap((update) => {
        const sessions = updateItem<ClosSession>(
          (session) => session.id === payload.session.id,
          (current) => ({
            ...current,
            ...update,
          }),
        );

        setState(patch({ sessions }));
      }),
    );
  }

  @Action(RefreshMediaToken)
  protected onRefreshMediaToken(
    { setState }: StateContext<RemoteSessionsStateModel>,
    { payload }: RefreshMediaToken,
  ) {
    return this.api.refreshMediaToken(payload.sessionId, payload.clientSessionId).pipe(
      // The request answers once; do not wait for a completion the token-refresh interceptor path never sends
      take(1),
      timeout(REFRESH_MEDIA_TOKEN_TIMEOUT_MS),
      tap((media) =>
        setState(
          patch({
            // Only the media changes; a session that is no longer stored stays absent
            sessions: updateItem<ClosSession>(
              ({ id }) => id === payload.sessionId,
              (current) => ({ ...current, media }),
            ),
          }),
        ),
      ),
    );
  }

  @Action(EnsureSessionInviteLinks)
  protected onEnsureSessionInviteLinks(
    { getState, setState }: StateContext<RemoteSessionsStateModel>,
    { payload }: EnsureSessionInviteLinks,
  ) {
    const session = getState().sessions.find(({ id }) => id === payload.session.id);
    const user = this.store.selectSnapshot(AuthState.user);
    if (
      !environment.roomInvitesOnDemand ||
      !session ||
      session.hasShortInviteLinks ||
      session.ownerId !== user?.id
    ) {
      return;
    }
    return this.api.issueSessionInvites(session.id).pipe(
      tap((links) => {
        if (!RemoteSessionsApi.hasShortInviteLinks(links)) {
          throw new Error('Invite links are not ready. Try sharing again.');
        }
        setState(
          patch({
            sessions: updateItem<ClosSession>(
              ({ id }) => id === session.id,
              (current) => ({
                ...current,
                unassignedLink: links.generic,
                link: links.generic,
                assignmentLinks: {
                  photograph: links.by_role?.photograph || '',
                  model: links.by_role?.model || '',
                  observer: links.by_role?.observer || '',
                },
                hasShortInviteLinks: true,
              }),
            ),
          }),
        );
      }),
    );
  }

  @Action(FetchInvitation)
  protected onFetchInvitation(
    { setState }: StateContext<RemoteSessionsStateModel>,
    { payload }: FetchInvitation,
  ) {
    return this.api.getSession({ sessionId: payload.session.id }).pipe(
      tap((session) => {
        setState(
          patch({
            invitations: append<ClosSessionInvitation>([{ session }]),
          }),
        );
      }),
    );
  }

  @Action(DeclineInvitation)
  protected onDeleteInvitation(
    { setState }: StateContext<RemoteSessionsStateModel>,
    { payload }: FetchInvitation,
  ) {
    return setState(
      patch({
        invitations: removeItem<ClosSessionInvitation>((invitation) => {
          return invitation.session.id === payload.session.id;
        }),
      }),
    );
  }
}
